import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  EventEmitter,
  Output,
  HostListener,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { IonicModule } from '@ionic/angular';
import { Comparable, ComparablesKey } from '@smartflip/data-models';
import { compare } from '@smartflip/utils-data';
import {
  comparablesColumns,
  comparablesHiddenColumns,
} from '@smartflip/data-constants';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { statusIconMap } from '../comparables.constants';
import { restoreHiddenColumns } from './comparables-table.helper';
import { StatusLegendComponent } from '../comparables-status-legend/statusLegend.component';

@Component({
  selector: 'app-comparables-table',
  templateUrl: './comparables-table.component.html',
  styleUrls: ['./comparables-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', paddingBottom: '1rem' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
      transition(
        'expanded <=> void',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  imports: [CommonModule, FormsModule, IonicModule, StatusLegendComponent],
})
export class ComparablesTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input()
  compsSource: Comparable[] = [];
  @Input()
  highlightedAddress!: string | null;
  @Output() selectionChange: EventEmitter<SelectionModel<Comparable>> =
    new EventEmitter();
  @Output() hoverChange: EventEmitter<Comparable> = new EventEmitter();
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.showHideColumnsPerSize();
  }

  columnsToDisplay = comparablesColumns;
  expandedElement!: Comparable | null;
  // TODO: Ensure this is fully functioning
  compsTableSource: Comparable[] = [];

  public compSelection = new SelectionModel<Comparable>(true, []);
  public showStatusHeader = false;
  public isBigColumn = (columnName: string) =>
    columnName === 'street' ? 3 : columnName === 'lastSoldPrice' ? 2 : 1;
  public sortState: { active: string; direction: string } = {
    active: 'street',
    direction: 'asc',
  };

  public headerLookup = {
    salesStatus: 'Status',
    street: 'Address',
    distanceFromSource: 'Dist.',
    lastSoldPrice: 'Last Sale',
    finishedSqFt: 'SqFt',
    bedrooms: 'Beds',
    bathrooms: 'Baths',
    selectBox: 'Select',
  };

  constructor(private currencyPipe: CurrencyPipe) {}

  ngOnInit(): void {
    this.compsTableSource = this.compsSource;
    this.compSelection.changed.subscribe(() =>
      this.selectionChange.emit(this.compSelection),
    );
    this.showHideColumnsPerSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.highlightedAddress?.currentValue) {
      this.highlightedAddress = changes.highlightedAddress.currentValue;
    }
  }

  ngAfterViewInit() {
    // TODO: Create new sort feature
    // this.compsTableSource.sort = this.sort;
  }

  private handleSortChange() {
    const { active, direction } = this.sortState;

    if (!active || direction === '' || active === 'selectBox') {
      this.compsTableSource = this.compsSource;
      return;
    }

    this.compsTableSource.sort((a, b) =>
      compare(a[active], b[active], direction === 'asc'),
    );
    this.expandedElement = null;
  }

  public getIcon(status: string) {
    return statusIconMap[status as keyof typeof statusIconMap];
  }

  public getIonicIcon(status: string): string {
    const iconMap = {
      'For Sale': 'home',
      'Coming Soon': 'time',
      'Off Market': 'eye-off',
      Rental: 'business',
      Sold: 'checkmark-circle',
      Pending: 'hourglass',
    };

    return iconMap[status as keyof typeof iconMap] || 'help';
  }

  public getColumnSort(column: string): string {
    const { active, direction } = this.sortState;
    const classDirection =
      direction.charAt(0).toUpperCase() + direction.slice(1);

    return active === column ? `--${classDirection}` : '';
  }

  public handleColumnSort(column: string) {
    // Implement sorting logic here if needed
    this.sortState = {
      active: column,
      direction: this.sortState.direction === 'asc' ? 'desc' : 'asc',
    };
    this.handleSortChange();
  }

  public alignRight(column: string): boolean {
    return column === 'lastSoldPrice' || column === 'selectBox';
  }

  /**
   * @param column the column to format
   * @param property the property to format
   * @returns the formatted value
   */
  public formatColumnData(column: string, property: Comparable): string {
    const columnKey = column as ComparablesKey;
    const valueToPipe = property[columnKey] || '';
    let returnValue = '';

    switch (column) {
      case 'lastSoldPrice':
        returnValue =
          this.currencyPipe.transform(valueToPipe, 'USD', 'symbol', '1.0-0') ||
          '';
        break;
      case 'distanceFromSource':
        returnValue = `${valueToPipe} mi`;
        break;
      default:
        returnValue =
          typeof valueToPipe === 'string' ? valueToPipe : String(valueToPipe);
    }

    return returnValue;
  }

  private showHideColumnsPerSize() {
    const screenWidth = window.innerWidth;
    const hiddenColumns = comparablesHiddenColumns;

    if (screenWidth < 700) {
      this.showStatusHeader = false;
      this.columnsToDisplay = this.columnsToDisplay.filter(
        (col) => !hiddenColumns.includes(col),
      );
    } else if (this.columnsToDisplay.length < 6) {
      // screenWidth is big enough, but also confirm that the columns weren't already hidden
      restoreHiddenColumns(this.columnsToDisplay);
    }
    if (screenWidth > 699) {
      this.showStatusHeader = true;
    }
  }

  /** The label for the checkbox on the passed row */
  // public checkboxLabel(row?: Comparable, index: number = 0): string {
  //   return `${this.compSelection.isSelected(row) ? 'deselect' : 'select'} row ${
  //     index + 1
  //   }`;
  // }

  onHoverChange(comparable: Comparable) {
    this.hoverChange.emit(comparable);
  }
}
